import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Container,
  Fade,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import {
  readinessQuestions,
  readinessTranslations,
  MAX_FRICTION,
  getFrictionScore,
  optimalAnswers,
  countOptimalAnswers,
  getResultTier,
  tierInsights,
  Language,
} from '../data/readiness-questions';
import {
  trackQuizLandingView,
  trackQuizStart,
  trackQuizQuestionView,
  trackQuizQuestionAnswered,
  trackQuizMilestone,
  trackQuizBackButton,
  trackQuizNextButton,
  trackQuizContactFormView,
  trackQuizFormFieldFocus,
  trackQuizFormFieldComplete,
  trackQuizPrivacyPolicyView,
  trackQuizPrivacyPolicyClose,
  trackQuizPrivacyConsent,
  trackQuizFormSubmitAttempt,
  trackQuizFormError,
  trackQuizComplete,
  trackQuizAbandon,
  trackQuizFinish,
  trackQuizLanguageToggle,
  trackCampaignSource,
  trackCTAClick,
  trackEvent,
  setQuizType,
  setUserId,
} from '../utils/analytics';
import { loadMetaPixel, trackMetaPixelLead } from '../utils/meta-pixel';

const CAMPAIGN_SOURCE = 'readiness_campaign';
// Separates this funnel from the lead qualifier at /guide, which emits the
// same event names.
const QUIZ_TYPE = 'readiness';
const QUESTION_TYPE = 'single_choice';
const MILESTONES = [25, 50, 75, 100];

const captureCampaignParams = () => {
  const params = new URLSearchParams(window.location.search);
  const email = params.get('uemail');
  const campaign = params.get('utm_campaign');
  const source = params.get('utm_source');
  const medium = params.get('utm_medium');
  const content = params.get('utm_content');
  const term = params.get('utm_term');

  if (email || campaign || source) {
    if (email) {
      sessionStorage.setItem('campaign_email', email);
      setUserId(email);
    }
    if (campaign) sessionStorage.setItem('utm_campaign', campaign);
    if (source) sessionStorage.setItem('utm_source', source);
    if (medium) sessionStorage.setItem('utm_medium', medium);
    if (content) sessionStorage.setItem('utm_content', content);
    if (term) sessionStorage.setItem('utm_term', term);

    trackCampaignSource({
      email: email || undefined,
      campaign: campaign || undefined,
      source: source || undefined,
      medium: medium || undefined,
    });

    if (email) {
      params.delete('uemail');
      const newUrl = params.toString()
        ? `${window.location.pathname}?${params}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }
};

type Step = 'welcome' | 'quiz' | 'contact' | 'done';

const ReadinessQuizPage: React.FC = () => {
  const [language, setLanguage] = useState<Language>('he');
  const [step, setStep] = useState<Step>('welcome');
  const [isTransitioning, setIsTransitioning] = useState(false);
  // Question id -> chosen option key. An id missing from the map is unanswered,
  // which is also what greys out the Next button.
  const [selected, setSelected] = useState<Record<string, string>>({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  const [contactForm, setContactForm] = useState({
    fullName: '',
    email: '',
    phone: '',
  });
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  const campaignCapturedRef = useRef(false);

  // --- Analytics bookkeeping -------------------------------------------------
  // When the page opened and when the current question came on screen, so every
  // event can carry how long it took rather than only that it happened.
  const pageOpenedAtRef = useRef(Date.now());
  const questionShownAtRef = useRef(Date.now());
  const formShownAtRef = useRef(0);
  const privacyOpenedAtRef = useRef(0);
  // Fire-once guards: progress milestones, per-field completion, and the
  // abandon event, which must not repeat if pagehide fires more than once.
  const milestonesFiredRef = useRef<Set<number>>(new Set());
  const fieldsCompletedRef = useRef<Set<string>>(new Set());
  const abandonReportedRef = useRef(false);
  // Mirror of the state the abandon event needs. The pagehide listener is
  // registered once, so it would otherwise close over the values from the
  // first render and report everyone as leaving on the welcome screen.
  const progressRef = useRef({
    step: 'welcome' as Step,
    questionIndex: 0,
    answered: 0,
    fieldsFilled: 0,
    language: 'he' as Language,
  });

  const isRTL = language === 'he';
  const t = readinessTranslations[language];

  // Dark editorial styling built on the Federman palette from the MUI theme:
  // primary #0693e3 / #38a5e8 / #0578b8 over secondary.dark #1d2024.
  //
  // Every value below is measured against the surface it actually lands on
  // rather than eyeballed, because the page has to stay readable with low
  // vision. Contrast ratios against the #1d2024 field:
  //   text       #f8f9fa            15.9:1
  //   textMuted  white @ 0.64        7.0:1  — body copy
  //   textFaint  white @ 0.60        6.2:1  — was 0.40 (3.6:1), below AA
  //   accent     #0693e3             4.9:1  — accent text, links
  //   accentLight #38a5e8            6.0:1  — focus ring, selected labels
  // White-on-blue is the one place the palette fights the requirement: white
  // over #0693e3 is only 3.3:1, so the CTA gradient runs over the darker two
  // blues (#0578b8 is 4.8:1 under white) instead of the brightest one.
  const ui = {
    // Heebo across the page. Scoped to this route rather than the MUI theme:
    // the theme's Arial is shared with the 360 assessment and the admin app,
    // and this brief was about /ready.
    font: "'Heebo', 'Arial', 'Helvetica', sans-serif",
    bg: '#1d2024',
    surface: 'rgba(255, 255, 255, 0.04)',
    surfaceRaised: 'rgba(255, 255, 255, 0.08)',
    // Control boundaries need 3:1 of their own. White @ 0.10 gave 1.4:1 against
    // the card fill, which is a hairline you cannot find without good sight.
    border: 'rgba(255, 255, 255, 0.42)',
    borderStrong: 'rgba(255, 255, 255, 0.70)',
    accent: '#0693e3',
    accentLight: '#38a5e8',
    accentSoft: 'rgba(6, 147, 227, 0.20)',
    text: '#f8f9fa',
    textMuted: 'rgba(248, 249, 250, 0.64)',
    textFaint: 'rgba(248, 249, 250, 0.60)',
    // Small tracked-out label. Tracked-out small caps are hard to read shrunk
    // down, so the eyebrow sits at body size rather than below it.
    eyebrow: {
      fontSize: '0.95rem',
      fontWeight: 700,
      // Hebrew letterforms are not drawn to be tracked out as far as Latin ones.
      letterSpacing: isRTL ? '0.08em' : '0.16em',
      color: '#38a5e8',
    },
    // Primary call to action: accent gradient with a soft glow, kept to the
    // darker end of the palette so the white label clears AA on every stop.
    cta: {
      // The solid colour matters as much as the gradient: MUI's contained
      // button paints theme primary (#0693e3, only 3.3:1 under white)
      // underneath, which is what shows through if the gradient does not.
      bgcolor: '#0578b8',
      backgroundImage: 'linear-gradient(135deg, #04649b 0%, #0578b8 100%)',
      color: '#fff',
      boxShadow: '0 10px 30px -8px rgba(6, 147, 227, 0.55)',
      // Darkened on hover rather than brightened: the lighter blues drop the
      // white label below AA.
      '&:hover': {
        bgcolor: '#04649b',
        backgroundImage: 'linear-gradient(135deg, #03547f 0%, #04649b 100%)',
      },
    },
  };

  // Initial setup
  useEffect(() => {
    // Scoped to this page, not index.html — the campaign that feeds /ready is
    // the only traffic the pixel is meant to see.
    loadMetaPixel();

    // Before any event is sent: this page and /guide emit the same event
    // names, and without the type they land in GA4 as one merged funnel.
    setQuizType(QUIZ_TYPE);

    if (!campaignCapturedRef.current) {
      campaignCapturedRef.current = true;
      captureCampaignParams();
    }

    // Allow ?lang=en to override default
    const params = new URLSearchParams(window.location.search);
    const langParam = params.get('lang');
    if (langParam === 'en' || langParam === 'he') {
      setLanguage(langParam);
    }

    trackQuizLandingView(language);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Page title
  useEffect(() => {
    document.title =
      language === 'he'
        ? 'מבחן מוכנות 8 שאלות | פדרמן יועצים'
        : '8-Question Readiness Quiz | Federman Consultants';
  }, [language]);

  // The app's body is white, and it shows through wherever this page's own box
  // does not reach: the rubber-band area past the top and bottom of a mobile
  // scroll, and the strip a collapsing address bar uncovers. Paint the document
  // itself for as long as the quiz is on screen, and hand it back on the way out
  // so the rest of the app keeps its own background.
  useEffect(() => {
    const { documentElement, body } = document;
    const previous = [documentElement.style.backgroundColor, body.style.backgroundColor];
    documentElement.style.backgroundColor = '#1d2024';
    body.style.backgroundColor = '#1d2024';
    return () => {
      documentElement.style.backgroundColor = previous[0];
      body.style.backgroundColor = previous[1];
    };
  }, []);

  // One view event per question, on every arrival — including arrivals by way
  // of the back button. This is what turns "8 people started, 3 finished" into
  // "they leave on question 5".
  useEffect(() => {
    if (step !== 'quiz') return;
    const question = readinessQuestions[currentQuestionIndex];
    questionShownAtRef.current = Date.now();
    trackQuizQuestionView(
      String(currentQuestionIndex + 1),
      question.id,
      QUESTION_TYPE,
      readinessQuestions.length,
      currentQuestionIndex,
      language,
    );
    // language is deliberately not a dependency: switching language mid-quiz
    // is its own event and should not look like a second view of the question.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, currentQuestionIndex]);

  // Timestamp for the contact step, so quiz_complete can carry how long the
  // form took to fill.
  useEffect(() => {
    if (step === 'contact') formShownAtRef.current = Date.now();
  }, [step]);

  // Keep the abandon mirror current. Runs after every render, on purpose.
  useEffect(() => {
    progressRef.current = {
      step,
      questionIndex: currentQuestionIndex,
      answered: Object.keys(selected).length,
      fieldsFilled: Object.values(contactForm).filter((v) => v.trim() !== '').length,
      language,
    };
  });

  // Leaving without finishing. pagehide is the unload event mobile Safari
  // actually delivers, and the beacon transport lets the request outlive the
  // page. Guarded so a second pagehide cannot double-count the same visitor.
  useEffect(() => {
    const handlePageHide = () => {
      const progress = progressRef.current;
      if (progress.step === 'done' || abandonReportedRef.current) return;
      abandonReportedRef.current = true;
      trackQuizAbandon({
        step: progress.step,
        questionIndex: progress.step === 'quiz' ? progress.questionIndex : undefined,
        questionsAnswered: progress.answered,
        totalQuestions: readinessQuestions.length,
        reachedForm: progress.step === 'contact',
        formFieldsFilled: progress.step === 'contact' ? progress.fieldsFilled : undefined,
        timeOnPageMs: Date.now() - pageOpenedAtRef.current,
        language: progress.language,
      });
    };
    window.addEventListener('pagehide', handlePageHide);
    return () => window.removeEventListener('pagehide', handlePageHide);
  }, []);

  const isValidEmail = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const transition = (cb: () => void) => {
    setIsTransitioning(true);
    setTimeout(() => {
      cb();
      setIsTransitioning(false);
    }, 150);
  };

  // Two readings of the same answers. The count of optimal answers (0-8) is
  // what the closing screen speaks to; the friction score (0-16) is the finer
  // one, kept for Avi.
  const optimalCount = countOptimalAnswers(selected);
  const frictionScore = getFrictionScore(selected);
  const tier = getResultTier(optimalCount);
  const insight = tierInsights[tier][language];

  const handleSubmitQuiz = () => {
    trackCTAClick('readiness_quiz_submit', language);
    // quiz_start belongs to the start button on the welcome screen. It used to
    // fire here as well, which counted every finisher as a second start and
    // made the first step of the funnel unreadable.
    trackEvent('readiness_quiz_score', {
      optimal_answers: optimalCount,
      friction_score: frictionScore,
      max_friction: MAX_FRICTION,
      tier,
      total: readinessQuestions.length,
      language,
    });
    // Every finisher lands on the same closing screen and form now — the score
    // is recorded for Avi, it no longer decides who gets asked to leave details.
    trackQuizContactFormView(language);
    transition(() => setStep('contact'));
  };

  // How long the policy stayed open says whether it was read or dismissed —
  // and it is the last thing some people do before leaving the form.
  const closePrivacyModal = () => {
    trackQuizPrivacyPolicyClose(Date.now() - privacyOpenedAtRef.current, language);
    setPrivacyModalOpen(false);
  };

  // A field counts as filled the first time it is left with something in it.
  // Paired with the focus event, this says which field people reach and then
  // leave empty — the one thing a submit-attempt event cannot tell you.
  const handleFieldBlur = (fieldName: string, value: string) => {
    if (!value.trim() || fieldsCompletedRef.current.has(fieldName)) return;
    fieldsCompletedRef.current.add(fieldName);
    trackQuizFormFieldComplete(fieldName, language);
  };

  const handleContactSubmit = async () => {
    const hasAllFields = !!(contactForm.fullName.trim() && contactForm.email && contactForm.phone);
    trackQuizFormSubmitAttempt(hasAllFields, privacyConsent, language);

    if (!hasAllFields || !privacyConsent) return;

    if (!isValidEmail(contactForm.email)) {
      trackQuizFormError('invalid_email', language);
      setEmailError(language === 'he' ? 'כתובת אימייל לא תקינה' : 'Invalid email address');
      return;
    }
    setEmailError(null);

    setIsSubmitting(true);
    setSubmitError(null);

    // Build answers payload: question id -> the option key that was chosen.
    // Keys travel, not labels, so a reworded answer does not orphan old leads.
    const answers: Record<string, string> = {};
    readinessQuestions.forEach((q) => {
      answers[q.id] = selected[q.id] || 'unanswered';
    });

    // The form asks for one name; the lead record and both emails are built
    // around a first and last name, so the split happens here rather than
    // making everyone downstream deal with a single string. First word is the
    // first name, whatever is left is the last name — and a single-word name
    // simply has none.
    const [firstName, ...restOfName] = contactForm.fullName.trim().split(/\s+/);

    try {
      const submitQuizLead = httpsCallable(functions, 'submitQuizLead');
      await submitQuizLead({
        answers: {
          ...answers,
          _readiness_optimal: optimalCount,
          _readiness_total: readinessQuestions.length,
          _readiness_tier: tier,
          _readiness_friction: frictionScore,
        },
        contact: {
          firstName,
          lastName: restOfName.join(' '),
          email: contactForm.email,
          phone: contactForm.phone,
        },
        language,
        source: CAMPAIGN_SOURCE,
      });

      trackQuizComplete(readinessQuestions.length, readinessQuestions.length, language, {
        optimal_answers: optimalCount,
        friction_score: frictionScore,
        tier,
        time_on_form_ms: Date.now() - formShownAtRef.current,
        time_total_ms: Date.now() - pageOpenedAtRef.current,
      });
      // After the lead is stored, not on the submit click: this is the event
      // the Meta campaign optimises against.
      trackMetaPixelLead(tier);
      transition(() => setStep('done'));
    } catch (error) {
      console.error('Error submitting readiness quiz lead:', error);
      trackQuizFormError('submit_error', language);
      setSubmitError(language === 'he' ? 'שגיאה בשליחה. נסה שוב.' : 'Error submitting. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const textFieldStyle = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      bgcolor: ui.surface,
      color: ui.text,
      '& fieldset': { borderColor: ui.border, borderWidth: 1.5 },
      '&:hover fieldset': { borderColor: ui.borderStrong },
      '&.Mui-focused fieldset': { borderColor: ui.accent, borderWidth: 2 },
    },
    '& .MuiInputLabel-root': { color: ui.textFaint },
    '& .MuiInputLabel-root.Mui-focused': { color: ui.accentLight },
    '& .MuiOutlinedInput-input::placeholder': { color: ui.textFaint },
    // The MUI theme is LTR, so an outlined label is anchored to left: 0 and
    // floats into a notch cut on the left. In Hebrew that puts the label on
    // the wrong side of the field and away from the right-aligned value.
    // Anchor it to the right and open the notch there instead.
    ...(isRTL && {
      '& .MuiInputLabel-root': {
        color: ui.textFaint,
        left: 'auto',
        right: 0,
        transformOrigin: 'top right',
        transform: 'translate(-14px, 16px) scale(1)',
        '&.MuiInputLabel-shrink': {
          transform: 'translate(-14px, -9px) scale(0.75)',
        },
        '&.Mui-focused': { color: ui.accentLight },
      },
      '& .MuiOutlinedInput-notchedOutline': { textAlign: 'right' },
    }),
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        // Mobile browsers grow and shrink the viewport as the address bar
        // hides, and 100vh is frozen at the tall measurement, so the page came
        // up short of the screen. dvh follows the live viewport.
        '@supports (min-height: 100dvh)': { minHeight: '100dvh' },
        bgcolor: ui.bg,
        color: ui.text,
        // Inherited by every control too, which MUI otherwise leaves on the
        // theme font.
        fontFamily: ui.font,
        '& .MuiTypography-root, & .MuiButton-root, & .MuiInputBase-root, & .MuiFormLabel-root': {
          fontFamily: ui.font,
        },
        display: 'flex',
        flexDirection: 'column',
        // Two soft accent glows keep the dark field from reading as flat black.
        // Not background-attachment: fixed — iOS Safari then paints the whole
        // background only within the viewport box and leaves the rest of the
        // element bare, which is what showed as white below the content.
        backgroundImage:
          'radial-gradient(900px 500px at 50% -10%, rgba(6, 147, 227, 0.20), transparent 70%),' +
          'radial-gradient(700px 400px at 100% 100%, rgba(56, 165, 232, 0.10), transparent 70%)',
        backgroundRepeat: 'no-repeat',
        // One visible focus ring for every control, including the answer cards,
        // which are keyboard-operable radios rather than plain click targets.
        '& :focus-visible': {
          outline: `3px solid ${ui.accentLight}`,
          outlineOffset: '2px',
        },
      }}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Persistent brand header (visible on every step + mobile) */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          bgcolor: 'rgba(29, 32, 36, 0.82)',
          backdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${ui.border}`,
          py: { xs: 1.25, sm: 1.5 },
          px: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: { xs: 1.25, sm: 2 },
          flexDirection: isRTL ? 'row-reverse' : 'row',
        }}
      >
        {/* The logo artwork already draws the name and the role line, so there
            is no text block beside it — it read as the same words twice. The
            words still reach a screen reader through the alt text. */}
        <Box
          component="img"
          // The .jpg cannot hold transparency, so it carried its own white
          // sheet onto the dark header. This is the same artwork with a real
          // alpha channel, flattened to white the way the brand's reversed
          // logo is drawn — no plate around it, dark or light.
          src="/logo.png"
          alt={`${t.headerName}, ${t.headerRole}`}
          sx={{
            height: { xs: 40, sm: 52 },
            width: 'auto',
            filter: 'brightness(0) invert(1)',
          }}
        />
      </Box>

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Container maxWidth="sm" sx={{ flex: 1, display: 'flex', flexDirection: 'column', py: { xs: 2, sm: 4 } }}>
          <Fade in={!isTransitioning} timeout={200}>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              {/* WELCOME */}
              {step === 'welcome' && (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    py: { xs: 4, sm: 6 },
                    px: 2,
                  }}
                >
                  <Box
                    component="img"
                    src="/avi-federman.png"
                    alt={t.headerName}
                    sx={{
                      width: { xs: 120, sm: 140 },
                      height: { xs: 120, sm: 140 },
                      borderRadius: '50%',
                      objectFit: 'cover',
                      objectPosition: 'top center',
                      border: `3px solid ${ui.accent}`,
                      boxShadow: '0 0 0 10px rgba(6, 147, 227, 0.10), 0 20px 50px -12px rgba(6, 147, 227, 0.45)',
                      mb: 4,
                    }}
                  />
                  <Typography sx={{ ...ui.eyebrow, mb: 2 }}>{t.landingSubtitle}</Typography>
                  <Typography
                    variant="h3" component="h1"
                    sx={{
                      fontWeight: 800,
                      color: ui.text,
                      fontSize: { xs: '2.1rem', sm: '3rem' },
                      lineHeight: 1.1,
                      letterSpacing: '-0.02em',
                      mb: 2.5,
                    }}
                  >
                    {t.welcomeTitle}
                  </Typography>
                  <Typography
                    sx={{
                      color: ui.textMuted,
                      fontSize: { xs: '1.1rem', sm: '1.25rem' },
                      lineHeight: 1.65,
                      maxWidth: 520,
                      mx: 'auto',
                      mb: 5,
                    }}
                  >
                    {t.welcomeMessage}
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => {
                      trackQuizStart(language);
                      transition(() => setStep('quiz'));
                    }}
                    sx={{
                      py: 2,
                      px: 6,
                      fontSize: '1.2rem',
                      fontWeight: 700,
                      borderRadius: '999px',
                      textTransform: 'none',
                      transition: 'all 0.2s ease-in-out',
                      ...ui.cta,
                      '@media (hover: hover) and (pointer: fine)': {
                        '&:hover': {
                          ...ui.cta['&:hover'],
                          transform: 'translateY(-2px)',
                          boxShadow: '0 16px 40px -10px rgba(6, 147, 227, 0.65)',
                        },
                      },
                      '&:active': { transform: 'translateY(0)' },
                    }}
                  >
                    {t.startButton}
                  </Button>

                  <Button
                    size="small"
                    onClick={() => {
                      const newLang: Language = language === 'he' ? 'en' : 'he';
                      trackQuizLanguageToggle(language, newLang);
                      setLanguage(newLang);
                    }}
                    sx={{ mt: 4, color: ui.textFaint, fontSize: '0.95rem', textTransform: 'none' }}
                  >
                    {language === 'he' ? 'Switch to English' : 'עברית'}
                  </Button>
                </Box>
              )}

              {/* QUIZ */}
              {step === 'quiz' && (() => {
                const currentQuestion = readinessQuestions[currentQuestionIndex];
                const currentAnswer = selected[currentQuestion.id];
                const hasAnsweredCurrent = currentAnswer !== undefined;
                const currentProgress = ((currentQuestionIndex + 1) / readinessQuestions.length) * 100;

                const handleAnswerSelect = (id: string, optionKey: string) => {
                  const isChange = selected[id] !== undefined && selected[id] !== optionKey;
                  const answeredAfter = { ...selected, [id]: optionKey };
                  setSelected(answeredAfter);

                  trackQuizQuestionAnswered(
                    String(currentQuestionIndex + 1),
                    id,
                    QUESTION_TYPE,
                    optionKey,
                    readinessQuestions.length,
                    currentQuestionIndex,
                    language,
                    {
                      is_optimal: optionKey === optimalAnswers[id],
                      is_change: isChange,
                      time_on_question_ms: Date.now() - questionShownAtRef.current,
                    },
                  );

                  // Progress milestones over how many questions are answered,
                  // each fired once. Redundant with the per-question events, but
                  // it is what makes a drop-off chart readable without building
                  // an exploration first.
                  const progressPercent =
                    (Object.keys(answeredAfter).length / readinessQuestions.length) * 100;
                  MILESTONES.forEach((milestone) => {
                    if (progressPercent >= milestone && !milestonesFiredRef.current.has(milestone)) {
                      milestonesFiredRef.current.add(milestone);
                      trackQuizMilestone(milestone, readinessQuestions.length, language);
                    }
                  });

                  if (currentQuestionIndex < readinessQuestions.length - 1) {
                    // Brief pause so the tap is confirmed, then fade across to the
                    // next question the same way every other step change does.
                    setTimeout(() => {
                      // Clamped inside the updater, not just by the check above:
                      // that check reads the index captured at render time, so two
                      // taps inside the 250ms window would both pass it and advance
                      // twice, running the index past the last question.
                      transition(() =>
                        setCurrentQuestionIndex((prev) =>
                          Math.min(prev + 1, readinessQuestions.length - 1),
                        ),
                      );
                    }, 250);
                  }
                };

                return (
                  <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                    {/* Progress Bar */}
                    <Box sx={{ mb: 4, width: '100%' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography sx={ui.eyebrow}>
                          {t.questionOf.replace('{current}', String(currentQuestionIndex + 1)).replace('{total}', String(readinessQuestions.length))}
                        </Typography>
                        <Typography sx={{ color: ui.textFaint, fontSize: '0.95rem', letterSpacing: '0.08em' }}>
                          {Math.round(currentProgress)}%
                        </Typography>
                      </Box>
                      <Box sx={{ width: '100%', height: 4, bgcolor: ui.surface, borderRadius: 999, overflow: 'hidden' }}>
                        <Box
                          sx={{
                            width: `${currentProgress}%`,
                            height: '100%',
                            backgroundImage: `linear-gradient(90deg, ${ui.accent}, ${ui.accentLight})`,
                            borderRadius: 999,
                            boxShadow: '0 0 12px rgba(6, 147, 227, 0.7)',
                            transition: 'width 0.3s ease-in-out',
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Question Card */}
                    <Box
                      sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexGrow: 0,
                        minHeight: { xs: 300, sm: 340 },
                        p: { xs: 3, sm: 4 },
                        border: `1px solid ${ui.border}`,
                        boxShadow: '0 24px 60px -24px rgba(0, 0, 0, 0.65)',
                        borderRadius: '24px',
                        bgcolor: ui.surface,
                        backdropFilter: 'blur(8px)',
                        mb: 4,
                      }}
                    >
                      <Typography
                        variant="h5" component="h1"
                        sx={{
                          fontWeight: 800,
                          color: ui.text,
                          textAlign: 'center',
                          lineHeight: 1.35,
                          letterSpacing: '-0.01em',
                          fontSize: { xs: '1.4rem', sm: '1.75rem' },
                          mb: 4,
                          minHeight: { xs: 50, sm: 70 },
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {currentQuestion.text[language]}
                      </Typography>

                      <Box
                        role="radiogroup"
                        aria-label={currentQuestion.text[language]}
                        sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%', maxWidth: 400 }}
                      >
                        {currentQuestion.options.map((option) => {
                          const isSelected = currentAnswer === option.key;
                          return (
                            <Box
                              key={`${currentQuestion.id}-${option.key}`}
                              role="radio"
                              tabIndex={0}
                              aria-checked={isSelected}
                              onClick={() => handleAnswerSelect(currentQuestion.id, option.key)}
                              onKeyDown={(e: React.KeyboardEvent) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handleAnswerSelect(currentQuestion.id, option.key);
                                }
                              }}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                textAlign: 'center',
                                py: 2,
                                px: 3,
                                border: isSelected
                                  ? `2px solid ${ui.accentLight}`
                                  : `1.5px solid ${ui.border}`,
                                bgcolor: isSelected ? ui.accentSoft : ui.surface,
                                // The label stays full-strength white either way:
                                // over the selected blue wash a tinted label would
                                // fall under AA, and selection is already carried by
                                // the border, the fill and the ring.
                                color: ui.text,
                                fontWeight: 600,
                                boxShadow: isSelected
                                  ? '0 0 0 4px rgba(6, 147, 227, 0.22), 0 16px 36px -16px rgba(6, 147, 227, 0.7)'
                                  : 'none',
                                borderRadius: '16px',
                                cursor: 'pointer',
                                userSelect: 'none',
                                transition: 'all 0.2s ease',
                                // Only real pointers get hover. On touch, Chrome keeps
                                // :hover on the tapped element until the next tap, so
                                // after the auto advance the card in that same position
                                // would still look selected on the new question. The
                                // colour is also kept distinct from the selected blue so
                                // hover never reads as "checked" on desktop either.
                                '@media (hover: hover) and (pointer: fine)': {
                                  '&:hover': {
                                    borderColor: isSelected ? ui.accentLight : ui.borderStrong,
                                    bgcolor: isSelected ? ui.accentSoft : ui.surfaceRaised,
                                    transform: 'scale(1.015)',
                                  },
                                },
                                '&:active': {
                                  transform: 'scale(0.985)',
                                },
                              }}
                            >
                              <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: 'inherit' }}>
                                {option.label[language]}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>

                    {/* Navigation Buttons */}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 2,
                        width: '100%',
                        mt: 'auto',
                      }}
                    >
                      <Button
                        variant="outlined"
                        onClick={() => {
                          if (currentQuestionIndex > 0) {
                            trackQuizBackButton(currentQuestionIndex, currentQuestionIndex - 1, language);
                            setCurrentQuestionIndex((prev) => prev - 1);
                          } else {
                            trackQuizBackButton(0, 'welcome', language);
                            transition(() => setStep('welcome'));
                          }
                        }}
                        sx={{
                          py: 1.5,
                          px: 3,
                          fontSize: '1rem',
                          borderRadius: '999px',
                          borderColor: ui.border,
                          color: ui.textMuted,
                          fontWeight: 600,
                          textTransform: 'none',
                          flex: 1,
                          maxWidth: 140,
                          '@media (hover: hover) and (pointer: fine)': {
                            '&:hover': {
                              borderColor: ui.borderStrong,
                              bgcolor: ui.surface,
                              color: ui.text,
                            },
                          },
                        }}
                      >
                        {t.backButton}
                      </Button>

                      <Button
                        variant="contained"
                        onClick={() => {
                          if (currentQuestionIndex < readinessQuestions.length - 1) {
                            // Reached by pressing Next rather than by picking an
                            // answer, which only happens after going back.
                            trackQuizNextButton(currentQuestionIndex, currentQuestionIndex + 1, language);
                            setCurrentQuestionIndex((prev) =>
                              Math.min(prev + 1, readinessQuestions.length - 1),
                            );
                          } else {
                            handleSubmitQuiz();
                          }
                        }}
                        disabled={!hasAnsweredCurrent}
                        sx={{
                          py: 1.5,
                          px: 4,
                          fontSize: '1rem',
                          borderRadius: '999px',
                          fontWeight: 700,
                          textTransform: 'none',
                          flex: 2,
                          maxWidth: 240,
                          ...ui.cta,
                          '@media (hover: hover) and (pointer: fine)': {
                            '&:hover': {
                              ...ui.cta['&:hover'],
                              boxShadow: '0 14px 36px -10px rgba(6, 147, 227, 0.7)',
                            },
                          },
                          '&:disabled': {
                            backgroundImage: 'none',
                            bgcolor: ui.surface,
                            color: 'rgba(248, 249, 250, 0.55)',
                            border: `1px solid ${ui.border}`,
                            boxShadow: 'none',
                          },
                        }}
                      >
                        {currentQuestionIndex < readinessQuestions.length - 1 ? t.nextButton : t.submitButton}
                      </Button>
                    </Box>

                    <Button
                      size="small"
                      onClick={() => {
                        const newLang: Language = language === 'he' ? 'en' : 'he';
                        trackQuizLanguageToggle(language, newLang);
                        setLanguage(newLang);
                      }}
                      sx={{ mt: 4, color: ui.textFaint, fontSize: '0.95rem', alignSelf: 'center', textTransform: 'none' }}
                    >
                      {language === 'he' ? 'Switch to English' : 'עברית'}
                    </Button>
                  </Box>
                );
              })()}

              {/* CLOSING + CONTACT FORM — one ending for everyone, with a
                  reading of their own answers at the top so nobody leaves the
                  page empty-handed. Three segments over the count of optimal
                  answers (0-3, 4-5, 6-8), but the count itself is never shown:
                  it goes to Avi on the lead, while the reader gets the pattern
                  and the one thing worth checking. */}
              {step === 'contact' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                  <Box
                    sx={{
                      mb: 4,
                      p: { xs: 2.5, sm: 3 },
                      borderRadius: '16px',
                      bgcolor: ui.surfaceRaised,
                      border: `1.5px solid ${ui.border}`,
                      // A single accent rule on the reading edge, so the block
                      // reads as their result rather than as another form field.
                      [isRTL ? 'borderRightWidth' : 'borderLeftWidth']: '4px',
                      [isRTL ? 'borderRightColor' : 'borderLeftColor']: ui.accentLight,
                    }}
                  >
                    <Typography
                      sx={{
                        color: ui.text,
                        fontWeight: 800,
                        fontSize: { xs: '1.25rem', sm: '1.4rem' },
                        lineHeight: 1.35,
                        letterSpacing: '-0.01em',
                        mb: 1.5,
                      }}
                    >
                      {insight.title}
                    </Typography>
                    <Typography sx={{ color: ui.textMuted, fontSize: '1.02rem', lineHeight: 1.7 }}>
                      {insight.body}
                    </Typography>
                    <Typography
                      sx={{
                        color: ui.text,
                        fontSize: '1.02rem',
                        lineHeight: 1.7,
                        fontWeight: 600,
                        mt: 2,
                        pt: 2,
                        borderTop: `1px solid ${ui.border}`,
                      }}
                    >
                      {insight.focus}
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4" component="h1"
                    sx={{
                      fontWeight: 800,
                      mb: 2,
                      color: ui.text,
                      textAlign: 'center',
                      letterSpacing: '-0.02em',
                      fontSize: { xs: '1.6rem', sm: '2.1rem' },
                      lineHeight: 1.25,
                    }}
                  >
                    {t.closingTitle}
                  </Typography>
                  <Typography
                    sx={{
                      color: ui.textMuted,
                      mb: 3,
                      textAlign: 'center',
                      fontSize: '1.05rem',
                      lineHeight: 1.7,
                      maxWidth: 480,
                      mx: 'auto',
                    }}
                  >
                    {t.closingBody}
                  </Typography>
                  <Typography
                    sx={{
                      color: ui.accentLight,
                      mb: 1,
                      textAlign: 'center',
                      fontSize: '1.15rem',
                      fontWeight: 700,
                      lineHeight: 1.5,
                    }}
                  >
                    {t.closingQuestion}
                  </Typography>
                  <Typography
                    sx={{
                      ...ui.eyebrow,
                      letterSpacing: 'normal',
                      fontSize: '1.05rem',
                      color: ui.text,
                      mb: 3,
                      textAlign: 'center',
                    }}
                  >
                    {t.contactCTA}
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      label={`${t.fullName} *`}
                      value={contactForm.fullName}
                      onChange={(e) => setContactForm((f) => ({ ...f, fullName: e.target.value }))}
                      onFocus={() => trackQuizFormFieldFocus('full_name', language)}
                      onBlur={() => handleFieldBlur('full_name', contactForm.fullName)}
                      sx={textFieldStyle}
                    />
                    <TextField
                      label={`${t.email} *`}
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => {
                        setContactForm((f) => ({ ...f, email: e.target.value }));
                        if (emailError) setEmailError(null);
                      }}
                      onFocus={() => trackQuizFormFieldFocus('email', language)}
                      onBlur={() => handleFieldBlur('email', contactForm.email)}
                      error={!!emailError}
                      helperText={emailError}
                      sx={{
                        ...textFieldStyle,
                        '& .MuiFormHelperText-root': { textAlign: isRTL ? 'right' : 'left' },
                      }}
                    />
                    <TextField
                      label={`${t.phone} *`}
                      type="tel"
                      value={contactForm.phone}
                      onChange={(e) => setContactForm((f) => ({ ...f, phone: e.target.value }))}
                      onFocus={() => trackQuizFormFieldFocus('phone', language)}
                      onBlur={() => handleFieldBlur('phone', contactForm.phone)}
                      sx={textFieldStyle}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={privacyConsent}
                          onChange={(e) => {
                            trackQuizPrivacyConsent(e.target.checked, language);
                            setPrivacyConsent(e.target.checked);
                          }}
                          sx={{ color: ui.textFaint, '&.Mui-checked': { color: ui.accent } }}
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: '1rem', lineHeight: 1.6, color: ui.textMuted }}>
                          {t.privacyConsent.split(language === 'he' ? 'למדיניות הפרטיות' : 'privacy policy')[0]}
                          <Link
                            component="button"
                            type="button"
                            onClick={(e: React.MouseEvent) => {
                              e.preventDefault();
                              e.stopPropagation();
                              trackQuizPrivacyPolicyView(language);
                              privacyOpenedAtRef.current = Date.now();
                              setPrivacyModalOpen(true);
                            }}
                            sx={{ color: ui.accentLight, cursor: 'pointer', verticalAlign: 'baseline' }}
                          >
                            {t.privacyPolicy}
                          </Link>
                        </Typography>
                      }
                      sx={{ alignItems: 'flex-start', mt: 1, '& .MuiFormControlLabel-label': { mt: 0.5 } }}
                    />
                  </Box>

                  {submitError && (
                    <Typography sx={{ color: '#f87171', mt: 2, textAlign: 'center' }}>
                      {submitError}
                    </Typography>
                  )}

                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleContactSubmit}
                    disabled={
                      !contactForm.fullName.trim() ||
                      !contactForm.email ||
                      !contactForm.phone ||
                      !privacyConsent ||
                      isSubmitting
                    }
                    sx={{
                      py: 2,
                      mt: 3,
                      fontSize: '1.05rem',
                      fontWeight: 700,
                      borderRadius: '999px',
                      textTransform: 'none',
                      ...ui.cta,
                      '&:disabled': {
                        backgroundImage: 'none',
                        bgcolor: ui.surface,
                        color: ui.textFaint,
                        boxShadow: 'none',
                      },
                    }}
                  >
                    {isSubmitting ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : t.sendButton}
                  </Button>
                </Box>
              )}

              {/* DONE */}
              {step === 'done' && (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    textAlign: 'center',
                    px: 2,
                    py: { xs: 3, sm: 5 },
                  }}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      bgcolor: 'rgba(34, 197, 94, 0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    <CheckCircleOutlineIcon sx={{ fontSize: 32, color: '#22c55e' }} />
                  </Box>
                  <Typography variant="h4" component="h1" sx={{ fontWeight: 800, mb: 1, color: ui.text, letterSpacing: '-0.02em', fontSize: { xs: '1.75rem', sm: '2.25rem' } }}>
                    {t.thankYouTitle}
                  </Typography>
                  <Typography sx={{ color: ui.textMuted, mb: 4, fontSize: '1.05rem', maxWidth: 480, mx: 'auto' }}>
                    {t.thankYouSubtitle}
                  </Typography>

                  {/* Avi photo + bio */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 2,
                      mb: 4,
                      p: { xs: 2.5, sm: 3 },
                      bgcolor: ui.surface,
                      border: `1px solid ${ui.border}`,
                      borderRadius: '20px',
                      maxWidth: 520,
                      mx: 'auto',
                    }}
                  >
                    <Box
                      component="img"
                      src="/avi-federman.png"
                      alt={t.headerName}
                      sx={{
                        width: { xs: 100, sm: 120 },
                        height: { xs: 100, sm: 120 },
                        borderRadius: '50%',
                        objectFit: 'cover',
                        objectPosition: 'top center',
                        border: `3px solid ${ui.accent}`,
                      }}
                    />
                    <Box>
                      <Typography sx={{ fontWeight: 700, color: ui.text, fontSize: '1.1rem', mb: 0.5 }}>
                        {t.headerName}
                      </Typography>
                      <Typography sx={{ color: ui.textMuted, fontSize: '1.05rem', lineHeight: 1.7 }}>
                        {t.aviBio}
                      </Typography>
                    </Box>
                  </Box>

                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => {
                      trackQuizFinish(language);
                      window.location.href = 'https://federman.org.il';
                    }}
                    sx={{
                      py: 2,
                      px: 4,
                      fontSize: '1rem',
                      fontWeight: 700,
                      borderRadius: '999px',
                      textTransform: 'none',
                      ...ui.cta,
                      alignSelf: 'center',
                    }}
                  >
                    {t.visitSiteButton}
                  </Button>
                </Box>
              )}
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Privacy Modal */}
      <Dialog
        open={privacyModalOpen}
        onClose={() => closePrivacyModal()}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            direction: isRTL ? 'rtl' : 'ltr',
            // The dialog is portalled out of the page's box, so it does not
            // inherit the font from it.
            fontFamily: ui.font,
            '& .MuiTypography-root': { fontFamily: ui.font },
            bgcolor: '#23272c',
            backgroundImage: 'none',
            border: `1px solid ${ui.border}`,
            color: ui.text,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            // Same darker stops as the CTA: white over the two brighter blues
            // is 3.3:1 and 2.7:1, and this bar carries the dialog's only title.
            bgcolor: '#0578b8',
            backgroundImage: 'linear-gradient(135deg, #04649b 0%, #0578b8 100%)',
            color: '#fff',
            fontWeight: 700,
          }}
        >
          {language === 'he' ? 'מדיניות פרטיות' : 'Privacy Policy'}
          <IconButton onClick={() => closePrivacyModal()} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          {language === 'he' ? (
            <Box sx={{ textAlign: 'right', lineHeight: 1.8 }}>
              <Typography variant="body2" sx={{ mb: 2, color: ui.textMuted }}>
                <strong>איסוף מידע:</strong> אנו אוספים את המידע שאתה מספק לנו בטופס זה: שם, אימייל, טלפון ותחום העסק.
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: ui.textMuted }}>
                <strong>שימוש במידע:</strong> ליצירת קשר, להצעת שירותים ולשליחת תוכן עסקי. אפשר ליצור עמך קשר באמצעות אימייל, טלפון, SMS או וואטסאפ.
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: ui.textMuted }}>
                <strong>הסרה מרשימת התפוצה:</strong> ניתן להסיר את עצמך בכל עת על ידי פנייה אלינו.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'left', lineHeight: 1.8 }}>
              <Typography variant="body2" sx={{ mb: 2, color: ui.textMuted }}>
                <strong>Information Collection:</strong> We collect what you provide: name, email, phone, business field.
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: ui.textMuted }}>
                <strong>Use of Information:</strong> To contact you and offer services. Contact may be via email, phone, SMS, or WhatsApp.
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: ui.textMuted }}>
                <strong>Unsubscribe:</strong> You can opt out anytime by contacting us.
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ReadinessQuizPage;
